import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CachedIcon from "@mui/icons-material/Cached";
import { PayPalButtons } from "@paypal/react-paypal-js";
import { useNavigate } from "react-router-dom";
import { useTheme } from "styled-components";

const UploadArtworkPaymentSection = ({
  oneTimeFee,
  setSnackbarMessage,
  setSnackbarOpen,
  onPaymentSuccess,
  paySubmissionFee,
  setPageCurrentStep,
  handlePublishArtwork,
  membership,
  loadings,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [compareOpen, setCompareOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);

  const handleCompareOpen = () => setCompareOpen(true);
  const handleCompareClose = () => setCompareOpen(false);

  const handlePaymentOpen = () => setPaymentOpen(true);
  const handlePaymentClose = () => setPaymentOpen(false);

  const packageData = [
    {
      title: "One Time Fee",
      price: `$${oneTimeFee} plus gas fee`,
      description:
        "Pay once and upload your artwork instantly. Best for occasional submissions.",
      type: "oneTime",
    },
    {
      title: "Creator’s Alliance Program",
      price: "$30/month",
      subPrice:"(Annual $360)",
      description:
        "Unlimited uploads and premium exposure benefits with our membership.",
      type: "cap",
    },
  ];

  const tableData = [
    ["Price", `$${oneTimeFee} / one upload`, "$30 /month"],
    [
      "Gas fee*",
      "Variable, based on blockchain fuel costs.",
      "FREE",
      "*Gas fee: The payment required to process transactions and smart contracts on the Ethereum blockchain. It fluctuates depending on network demand and transaction complexity."
    ],
    ["Upload artwork", "1", "Unlimited"],
    ["Sales commission fee", "25%", "19%"],
    ["Topline Exposure", "X", "V"],
    ["Spotlight", "X", "V"],
    ["Fan club", "X", "V"],
    ["Customize art pricing", "X", "V"],
    ["Social media exposure", "X", "V"],
  ];


  const NavigationButtons = ()=>{
    return(
      <>
       {/* Navigation Buttons */}
       <Box
       sx={{
         display: "flex",
         justifyContent: "space-between",
         mt: 4,
       }}
     >
       <Button
         type="button"
         variant="contained"
         onClick={() => setPageCurrentStep("edit")}
         disabled={Object.values(loadings).some((val) => val)}
         sx={{
           backgroundColor: theme.colors.blue,
           color: "white",
           fontWeight: "bold",
           borderRadius: "50px",
           textTransform: "none",
           padding: "10px 30px",
           "&:hover": { backgroundColor: theme.colors.green },
         }}
       >
         <ArrowBackIcon sx={{ marginRight: "6px", fontSize: "1.2rem" }} /> Back
       </Button>

       <Button
         type="button"
         variant="contained"
         onClick={handlePublishArtwork}
         disabled={
           Object.values(loadings).some((val) => val) || paySubmissionFee
         }
         sx={{
           backgroundColor: theme.colors.green,
           color: "white",
           fontWeight: "bold",
           borderRadius: "50px",
           textTransform: "none",
           padding: "10px 30px",
           "&:hover": { backgroundColor: theme.colors.blue },
         }}
       >
         {Object.values(loadings).some((val) => val) && (
           <CachedIcon
             sx={{ marginRight: "6px", fontSize: "1.2rem" }}
             className="spin-animation"
           />
         )}
         Go Live
       </Button>
     </Box>
     </>
    )
  }
  if (membership.isActive) {
    return (
      <Box textAlign="center" mt={4}>
        <Typography variant="h4" fontWeight="bold" color="white">
          You're all set!
        </Typography>
        <Typography color="white">
          As a subscriber, processing fee is free-of-charge.
        </Typography>
        <NavigationButtons />
      </Box>
    );
  }

  if (!membership.isActive && !paySubmissionFee) {
    return (
      <Box textAlign="center" mt={4}>
        <Typography variant="h4" fontWeight="bold" color="white">
          You're all set!
        </Typography>
        <Typography color="white">
          You've paid the One-Time Fee, you can publish now.
        </Typography>
        <NavigationButtons />
      </Box>
    );
  }

  return (
    <Box>
      {/* Package Cards */}
      <Box
        sx={{
          display: "flex",
          gap: 3,
          flexWrap: "wrap",
          justifyContent: "center",
          mt: 4,
        }}
      >
        {packageData.map((pkg, index) => (
          <Card
            key={index}
            sx={{
              width: 300,
              borderRadius: "20px",
              backgroundColor: theme.colors?.mediumPurple || "#6a4fcf",
              color: "white",
              textAlign: "center",
              p: 3,
              boxShadow: 3,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              borderWidth: 1,
              borderColor: "white",
            }}
          >
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography
                variant="h6"
                sx={{ fontFamily: "basic-sans", fontWeight: 700, mb: 1 }}
              >
                {pkg.title}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
                {pkg.price}
                <Typography component="span" sx={{ fontSize: 12, ml: 1 }}>
                  {pkg?.subPrice}
                </Typography>
              </Typography>
              <Typography variant="body2" sx={{ fontSize: "0.95rem", mb: 3 }}>
                {pkg.description}
              </Typography>
            </CardContent>

            <Box sx={{ textAlign: "center", mt: "auto" }}>
              {pkg.type === "cap" ? (
                <Button
                  variant="contained"
                  onClick={() => navigate("/challenge/memberships/cap")}
                  sx={{
                    backgroundColor: "white",
                    color: theme.colors?.mediumPurple || "#6a4fcf",
                    borderRadius: "30px",
                    px: 4,
                    fontWeight: 700,
                    "&:hover": { backgroundColor: "#f5f5f5" },
                  }}
                >
                  Subscribe
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handlePaymentOpen}
                  sx={{
                    backgroundColor: "white",
                    color: theme.colors?.mediumPurple || "#6a4fcf",
                    borderRadius: "30px",
                    px: 4,
                    fontWeight: 700,
                    "&:hover": { backgroundColor: "#f5f5f5" },
                  }}
                >
                  Pay One-Time Fee
                </Button>
              )}
            </Box>
          </Card>
        ))}
      </Box>

      {/* Compare Price Button */}
      <Box sx={{ textAlign: "center", mt: 4 }}>
        <Button
          variant="outlined"
          onClick={handleCompareOpen}
          sx={{
            borderRadius: "30px",
            borderColor: "white",
            color: "white",
            px: 4,
            fontWeight: 700,
          }}
        >
          Compare Plans
        </Button>
      </Box>

      {/* Compare Packages Modal */}
      <Dialog open={compareOpen} onClose={handleCompareClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            fontWeight: "bold",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Compare Packages
          <IconButton onClick={handleCompareClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell sx={{fontSize:20, fontWeight: "bold",color:'red',fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif !important',textAlign:'center', }}>One Time Fee</TableCell>
                <TableCell sx={{fontSize:20, fontWeight: "bold",color:'red',fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif !important',textAlign:'center', }}>
                  Creator’s Alliance Program
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData.map((row, idx) => (
                <React.Fragment key={idx}>
                  <TableRow>
                    <TableCell>{row[0]}</TableCell>
                    <TableCell
                      sx={{
                        textAlign:'center',
                        color: row[1] === "X" ? "red" : row[1] === "V" ? "green" : "inherit",
                        fontWeight: row[1] === "X" || row[1] === "V" ? "bold" : "normal",
                      }}
                    >
                      {row[1]}
                    </TableCell>

                    <TableCell
                      sx={{
                        textAlign:'center',
                        color: row[2] === "X" ? "red" : row[2] === "V" ? "green" : "inherit",
                        fontWeight: row[2] === "X" || row[2] === "V" ? "bold" : "normal",
                      }}
                    >
                      {row[2]}
                    </TableCell>

                  </TableRow>
                  {row[3] && (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography
                          variant="body2"
                          sx={{ fontStyle: "italic", color: "gray" }}
                        >
                          {row[3]}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>

      {/* One-Time Fee Payment Modal */}
      <Dialog open={paymentOpen} onClose={handlePaymentClose} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{
            fontWeight: "bold",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Pay One-Time Fee
          <IconButton onClick={handlePaymentClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <PayPalButtons
            className="paypal-button-width"
            createOrder={(data, actions) => {
              const fee = parseFloat(oneTimeFee) || 0;
              if (fee <= 0) {
                setSnackbarMessage("Invalid submission fee amount.");
                setSnackbarOpen(true);
                return;
              }
              return actions.order.create({
                purchase_units: [{ amount: { value: fee.toFixed(2) } }],
              });
            }}
            onApprove={async (data, actions) => {
              try {
                const capture = await actions.order.capture();
                await onPaymentSuccess(capture);
                handlePaymentClose();
              } catch (error) {
                console.error("Payment failed:", error);
                setSnackbarMessage(
                  "An error occurred during payment. Please try again."
                );
                setSnackbarOpen(true);
              }
            }}
            onError={(err) => {
              console.error("PayPal error:", err);
              setSnackbarMessage(
                "An error occurred with PayPal. Please try again."
              );
              setSnackbarOpen(true);
            }}
          />
        </DialogContent>
      </Dialog>

     <NavigationButtons />
    </Box>
  );
};

export default UploadArtworkPaymentSection;
